// marked-places.ts — the registry the Random Page button picks from.
//
// Each entry is a single destination on the KTP site — a page, a section
// anchor inside a long body, a writing piece, or a language entry. The
// button selects one at random and navigates the reader there. The
// experience is deliberately untargeted; that is the point.
//
// All entries are internal KTP routes. External URLs (Medium essays, GitHub
// RFCs) were removed — the wander button stays on-site.
//
// Sources are aggregated from existing data files where possible so the
// registry stays in lockstep with the rest of the site. Granular marks
// (anchors inside long pages) are listed manually below; they are the
// curation surface that grows the registry over time.
//
// Speakeasy entries (kind "axiom" or "room") are tagged speakeasy: true.
// They only enter the random pool if the reader has crossed the door —
// the localStorage flag ktp.speakeasy.entered.v1 is the gate.
//
// The aspirational ceiling is 777. The current count is whatever the
// arrays below produce.

import { WRITING } from "./writing/articles";
import { STAGES } from "./research/stages";
import { PAPERS } from "./research/papers";
import { FALSIFIERS } from "./research/falsifiers";
import { ROOMS } from "./physics/rooms";
import { AXIOMS } from "./physics/axioms";
import { VOCAB } from "./language/vocabulary";

export type MarkKind =
  | "page"
  | "essay"
  | "rfc"
  | "voyage"
  | "room"
  | "axiom"
  | "stage"
  | "paper"
  | "falsifier"
  | "concept"
  | "anchor";

export type MarkedPlace = {
  /** Stable id. */
  id: string;
  /** Internal route. Hash anchors fine. */
  href: string;
  /** Short human-readable label. Not displayed in UI; useful for dev. */
  label: string;
  /** Kind. Used for filtering and balanced UX. */
  kind: MarkKind;
  /** True if reaching this place requires the speakeasy door. */
  speakeasy?: boolean;
  /** True if href is external (http/https). Kept for type compat; no
   *  external entries are included in the wander pool. */
  external?: boolean;
};

/* ── primary public pages ─────────────────────────────── */

const PRIMARY_PAGES: MarkedPlace[] = [
  { id: "p-home",       href: "/",          label: "Home",           kind: "page" },
  { id: "p-about",      href: "/about",      label: "About",          kind: "page" },
  { id: "p-start",      href: "/start",      label: "Start",          kind: "page" },
  { id: "p-learn",      href: "/learn",      label: "Learn",          kind: "page" },
  { id: "p-writing",    href: "/writing",    label: "Writing",        kind: "page" },
  { id: "p-community",  href: "/community",  label: "Community",      kind: "page" },
  { id: "p-language",   href: "/language",   label: "Language",       kind: "page" },
];

/* ── learn section ───────────────────────────────────────── */

const LEARN_PAGES: MarkedPlace[] = [
  { id: "p-learn-trust-tiers",         href: "/learn/trust-tiers",          label: "Trust Tiers",             kind: "page" },
  { id: "p-learn-lineage",             href: "/learn/lineage",              label: "Lineage",                 kind: "page" },
  { id: "p-learn-constitution",        href: "/learn/constitution",         label: "Constitution",            kind: "page" },
  { id: "p-learn-simulator",           href: "/learn/simulator",            label: "Simulator",               kind: "page" },
  { id: "p-learn-trust-force-eq",      href: "/learn/trust-force-equation", label: "Trust Force Equation",    kind: "page" },
];

/* ── specs section ───────────────────────────────────────── */

const SPECS_PAGES: MarkedPlace[] = [
  { id: "p-specs",            href: "/specs",             label: "Specs",       kind: "page" },
  { id: "p-specs-blue-zones", href: "/specs/blue-zones",  label: "Blue Zones",  kind: "page" },
];

/* ── implement ───────────────────────────────────────────── */

const IMPLEMENT_PAGES: MarkedPlace[] = [
  { id: "p-implement", href: "/implement", label: "Implement", kind: "page" },
];

/* ── enterprise section ──────────────────────────────────── */

const ENTERPRISE_PAGES: MarkedPlace[] = [
  { id: "p-enterprise",              href: "/governed-autonomy",               label: "Enterprise",              kind: "page" },
  { id: "p-enterprise-risks",        href: "/governed-autonomy/risks",         label: "Enterprise · Risks",      kind: "page" },
  { id: "p-enterprise-architecture", href: "/governed-autonomy/architecture",  label: "Enterprise · Architecture",kind: "page" },
  { id: "p-enterprise-roadmap",      href: "/governed-autonomy/roadmap",       label: "Enterprise · Roadmap",    kind: "page" },
  { id: "p-enterprise-ciso",         href: "/governed-autonomy/ciso",          label: "Enterprise · CISO",       kind: "page" },
  { id: "p-enterprise-methods",      href: "/governed-autonomy/methods",       label: "Enterprise · Methods",    kind: "page" },
  { id: "p-enterprise-skeptical",    href: "/governed-autonomy/skeptical",     label: "Enterprise · Skeptical",  kind: "page" },
];

/* ── research section ────────────────────────────────────── */

const RESEARCH_PAGES: MarkedPlace[] = [
  { id: "p-research",                          href: "/research",                           label: "Research",                        kind: "page" },
  { id: "p-research-roadmap",                  href: "/research/roadmap",                   label: "Research · Roadmap",              kind: "page" },
  { id: "p-research-papers",                   href: "/research/papers",                    label: "Research · Papers",               kind: "page" },
  { id: "p-research-methods",                  href: "/research/methods",                   label: "Research · Methods",              kind: "page" },
  { id: "p-research-falsification",            href: "/research/falsification",             label: "Research · Falsification",        kind: "page" },
  { id: "p-research-collaborate",              href: "/research/collaborate",               label: "Research · Collaborate",          kind: "page" },
  { id: "p-research-cartography",              href: "/research/cartography",               label: "Research · Cartography",          kind: "page" },
  { id: "p-research-cognitive",                href: "/research/cognitive",                 label: "Research · Cognitive",            kind: "page" },
  { id: "p-research-cognitive-dcti",           href: "/research/cognitive/dcti",            label: "Research · DCTI",                 kind: "page" },
  { id: "p-research-cognitive-installed",      href: "/research/cognitive/installed-watcher",label: "Research · Installed Watcher",   kind: "page" },
  { id: "p-research-cognitive-sources",        href: "/research/cognitive/sources",         label: "Research · IW Sources",           kind: "page" },
  { id: "p-research-cognitive-acpp",           href: "/research/cognitive/acpp",            label: "Research · ACPP",                 kind: "page" },
  { id: "p-research-shipwreck",                href: "/research/shipwreck",                 label: "Research · Shipwreck",            kind: "page" },
  { id: "p-research-identity-attractor",       href: "/research/identity-as-attractor",     label: "Research · Identity as Attractor",kind: "page" },
];

/* ── canon section ───────────────────────────────────────── */

const CANON_PAGES: MarkedPlace[] = [
  { id: "p-canon",                    href: "/canon",                          label: "Canon",                     kind: "page" },
  { id: "p-canon-stack",              href: "/canon/stack",                    label: "Canon · Stack",             kind: "page" },
  { id: "p-canon-claims",             href: "/canon/claims",                   label: "Canon · Claims",            kind: "page" },
  { id: "p-canon-limits",             href: "/canon/limits",                   label: "Canon · Limits",            kind: "page" },
  { id: "p-canon-glossary",           href: "/canon/glossary",                 label: "Canon · Glossary",          kind: "page" },
  { id: "p-canon-ethics",             href: "/canon/ethics",                   label: "Canon · Ethics",            kind: "page" },
  { id: "p-canon-synthesis",          href: "/canon/synthesis",                label: "Canon · Synthesis",         kind: "page" },
  { id: "p-canon-repair",             href: "/canon/repair",                   label: "Canon · Repair",            kind: "page" },
  { id: "p-canon-clusters",           href: "/canon/clusters",                 label: "Canon · Clusters",          kind: "page" },
  { id: "p-canon-stewart-brand",      href: "/canon/ancestors/stewart-brand",  label: "Canon · Stewart Brand",     kind: "page" },
];

/* ── advocacy section ────────────────────────────────────── */

const ADVOCACY_PAGES: MarkedPlace[] = [
  { id: "p-advocacy",                      href: "/advocacy",                          label: "Advocacy",                        kind: "page" },
  { id: "p-advocacy-playbook",             href: "/advocacy/playbook",                 label: "Advocacy · Playbook",             kind: "page" },
  { id: "p-advocacy-substrates",           href: "/advocacy/substrates",               label: "Advocacy · Substrates",           kind: "page" },
  { id: "p-advocacy-family",               href: "/advocacy/family",                   label: "Advocacy · Family",               kind: "page" },
  { id: "p-advocacy-culture",              href: "/advocacy/culture",                  label: "Advocacy · Culture",              kind: "page" },
  { id: "p-advocacy-measurement",          href: "/advocacy/measurement",              label: "Advocacy · Measurement",          kind: "page" },
  { id: "p-advocacy-law",                  href: "/advocacy/law",                      label: "Advocacy · Law",                  kind: "page" },
  { id: "p-advocacy-money",                href: "/advocacy/money",                    label: "Advocacy · Money",                kind: "page" },
  { id: "p-advocacy-k12",                  href: "/advocacy/k12",                      label: "Advocacy · K-12",                 kind: "page" },
  { id: "p-advocacy-k12-substrate-map",    href: "/advocacy/k12/substrate-map",        label: "Advocacy · K-12 Substrate Map",   kind: "page" },
  { id: "p-advocacy-k12-measurement",      href: "/advocacy/k12/measurement-reform",   label: "Advocacy · K-12 Measurement",     kind: "page" },
  { id: "p-advocacy-k12-phone-free",       href: "/advocacy/k12/phone-free-zones",     label: "Advocacy · Phone-Free Zones",     kind: "page" },
  { id: "p-advocacy-k12-interpretive",     href: "/advocacy/k12/interpretive-practice",label: "Advocacy · Interpretive Practice",kind: "page" },
  { id: "p-advocacy-k12-provisioning",     href: "/advocacy/k12/substrate-provisioning",label: "Advocacy · Substrate Provisioning",kind: "page" },
  { id: "p-advocacy-k12-voice",            href: "/advocacy/k12/voice-in-design",      label: "Advocacy · Voice in Design",      kind: "page" },
  { id: "p-advocacy-templates",            href: "/advocacy/templates",                label: "Advocacy · Templates",            kind: "page" },
  { id: "p-advocacy-scoreboard",           href: "/advocacy/scoreboard",               label: "Advocacy · Scoreboard",           kind: "page" },
  { id: "p-advocacy-kpis",                 href: "/advocacy/kpis",                     label: "Advocacy · KPIs",                 kind: "page" },
  { id: "p-advocacy-coalition",            href: "/advocacy/coalition",                label: "Advocacy · Coalition",            kind: "page" },
  { id: "p-advocacy-disclose",             href: "/advocacy/disclose",                 label: "Advocacy · Disclose",             kind: "page" },
  { id: "p-advocacy-contribute",           href: "/advocacy/contribute",               label: "Advocacy · Contribute",           kind: "page" },
  { id: "p-advocacy-decision-tree",        href: "/advocacy/decision-tree",            label: "Advocacy · Decision Tree",        kind: "page" },
  { id: "p-advocacy-opposition",           href: "/advocacy/opposition",               label: "Advocacy · Opposition",           kind: "page" },
  { id: "p-advocacy-existing-law",         href: "/advocacy/existing-law",             label: "Advocacy · Existing Law",         kind: "page" },
];

/* ── voyage chapters (internal routes) ────────────────── */

const VOYAGE_SLUGS = [
  "departure",
  "vessel",
  "stars",
  "swells",
  "current",
  "crew",
  "storm",
  "horizon",
  "sacred",
  "landfall",
  "archipelago",
  "fire",
];

const VOYAGE_PAGES: MarkedPlace[] = VOYAGE_SLUGS.map((slug) => ({
  id: `voyage-${slug}`,
  href: `/voyage/${slug}`,
  label: `Voyage · ${slug}`,
  kind: "voyage",
}));

/* ── internal writing articles ───────────────────────────── */
// Derived from WRITING filtered to platform === "internal". These are the
// articles that live on the KTP site at /writing/:slug. Medium-only
// articles (no platform or platform !== "internal") are excluded — they
// navigate off-site.

const INTERNAL_WRITING_PAGES: MarkedPlace[] = WRITING.filter(
  (w) => w.platform === "internal"
).map((w) => ({
  id: `writing-${w.id}`,
  href: w.url,
  label: w.title,
  kind: "essay" as MarkKind,
}));

/* ── language vocabulary entries ─────────────────────────── */

const LANGUAGE_PAGES: MarkedPlace[] = VOCAB.map((v) => ({
  id: `lang-${v.id}`,
  href: `/language/${v.id}`,
  label: `Language · ${v.id}`,
  kind: "concept" as MarkKind,
}));

/* ── speakeasy: rooms ─────────────────────────────────────── */

const ROOM_PAGES: MarkedPlace[] = ROOMS.map((r) => ({
  id: `room-${r.slug}`,
  href: r.slug === "axioms" ? "/physics/axioms" : `/physics/${r.slug}`,
  label: `Room · ${r.title}`,
  kind: "room" as MarkKind,
  speakeasy: true,
}));

/* ── speakeasy: axioms ────────────────────────────────────── */

const AXIOM_PAGES: MarkedPlace[] = AXIOMS.map((a) => ({
  id: `axiom-${a.n}`,
  href: `/physics/axioms/${a.n}`,
  label: `Axiom ${a.n} · ${a.title}`,
  kind: "axiom" as MarkKind,
  speakeasy: true,
}));

/* ── research: stages, papers, falsifiers ─────────────────── */

const STAGE_MARKS: MarkedPlace[] = STAGES.map((s) => ({
  id: `stage-${s.id}`,
  href: `/research/roadmap#${s.id.toLowerCase()}`,
  label: `Stage ${s.n} · ${s.title}`,
  kind: "stage" as MarkKind,
}));

const PAPER_MARKS: MarkedPlace[] = PAPERS.map((p) => ({
  id: `paper-${p.id}`,
  href: `/research/papers#${p.id.toLowerCase()}`,
  label: `Paper ${p.id}`,
  kind: "paper" as MarkKind,
}));

const FALSIFIER_MARKS: MarkedPlace[] = FALSIFIERS.map((f) => ({
  id: `falsifier-${f.id}`,
  href: `/research/falsification#test-${f.id}`,
  label: `Falsifier ${f.id} · ${f.title}`,
  kind: "falsifier" as MarkKind,
}));

/* ── curated anchor marks inside long bodies ──────────── */
// These are the manual additions. Grow this list to approach 777.
// Each entry corresponds to a real id={...} that should exist on the
// destination page. If an id is missing on the page, the browser will
// land at the top of the page — still a valid wander.

const ANCHOR_MARKS: MarkedPlace[] = [
  // Acequia (speakeasy room)
  { id: "a-acequia-ditch",   href: "/physics/acequia#the-ditch",          label: "Acequia · the ditch",        kind: "anchor", speakeasy: true },
  { id: "a-acequia-force",   href: "/physics/acequia#the-force",          label: "Acequia · the force",        kind: "anchor", speakeasy: true },
  { id: "a-acequia-proof",   href: "/physics/acequia#the-proof",          label: "Acequia · the proof",        kind: "anchor", speakeasy: true },
  { id: "a-acequia-counter", href: "/physics/acequia#the-counter-proofs", label: "Acequia · counter-proofs",   kind: "anchor", speakeasy: true },
  { id: "a-acequia-lit",     href: "/physics/acequia#the-literature",     label: "Acequia · the literature",   kind: "anchor", speakeasy: true },
  { id: "a-acequia-field",   href: "/physics/acequia#the-field",          label: "Acequia · the field",        kind: "anchor", speakeasy: true },

  // What Next (speakeasy)
  { id: "a-whatnext-diagnosis",  href: "/physics/what-next#the-diagnosis",       label: "What Next · diagnosis",       kind: "anchor", speakeasy: true },
  { id: "a-whatnext-five-steps", href: "/physics/what-next#the-five-steps",      label: "What Next · five steps",      kind: "anchor", speakeasy: true },
  { id: "a-whatnext-nine",       href: "/physics/what-next#the-nine-principles", label: "What Next · nine principles",  kind: "anchor", speakeasy: true },
  { id: "a-whatnext-phases",     href: "/physics/what-next#the-phase-plan",      label: "What Next · phase plan",      kind: "anchor", speakeasy: true },
  { id: "a-whatnext-substrate",  href: "/physics/what-next#the-substrate-work",  label: "What Next · substrate work",  kind: "anchor", speakeasy: true },

  // The Program (speakeasy)
  { id: "a-program-question",  href: "/physics/the-program#the-question",    label: "Program · the question",   kind: "anchor", speakeasy: true },
  { id: "a-program-synthesis", href: "/physics/the-program#the-synthesis",   label: "Program · the synthesis",  kind: "anchor", speakeasy: true },
  { id: "a-program-layers",    href: "/physics/the-program#the-layers",      label: "Program · the layers",     kind: "anchor", speakeasy: true },
  { id: "a-program-scale",     href: "/physics/the-program#the-scale",       label: "Program · the scale",      kind: "anchor", speakeasy: true },
  { id: "a-program-why",       href: "/physics/the-program#why-it-matters",  label: "Program · why it matters", kind: "anchor", speakeasy: true },
  { id: "a-program-impact",    href: "/physics/the-program#impact-surfaces", label: "Program · impact surfaces",kind: "anchor", speakeasy: true },

  // Research / Methods sections
  { id: "a-methods-recoding",    href: "/research/methods#recoding",       label: "Methods · recoding gate",      kind: "anchor" },
  { id: "a-methods-repro",       href: "/research/methods#reproducibility",label: "Methods · reproducibility",    kind: "anchor" },
  { id: "a-methods-statistical", href: "/research/methods#statistical",    label: "Methods · simulations",        kind: "anchor" },
  { id: "a-methods-reviewers",   href: "/research/methods#reviewers",      label: "Methods · reviewer recruitment",kind: "anchor" },
  { id: "a-methods-risks",       href: "/research/methods#risks",          label: "Methods · risks",              kind: "anchor" },

  // Research / Falsification per layer
  { id: "a-falsif-l1",  href: "/research/falsification#layer-l1",                          label: "Falsification · L1",               kind: "anchor" },
  { id: "a-falsif-l2",  href: "/research/falsification#layer-l2",                          label: "Falsification · L2",               kind: "anchor" },
  { id: "a-falsif-l3",  href: "/research/falsification#layer-l3",                          label: "Falsification · L3",               kind: "anchor" },
  { id: "a-falsif-l4",  href: "/research/falsification#layer-l4",                          label: "Falsification · L4",               kind: "anchor" },
  { id: "a-falsif-not", href: "/research/falsification#what-these-tests-do-not-falsify",   label: "Falsification · what is not falsified", kind: "anchor" },

  // Specs layer rail
  { id: "a-specs-foundation", href: "/specs#layer-foundation", label: "Specs · foundation",        kind: "anchor" },
  { id: "a-specs-transport",  href: "/specs#layer-transport",  label: "Specs · transport",         kind: "anchor" },
  { id: "a-specs-sensors",    href: "/specs#layer-sensors",    label: "Specs · sensors",           kind: "anchor" },
  { id: "a-specs-enforce",    href: "/specs#layer-enforce",    label: "Specs · enforcement",       kind: "anchor" },
  { id: "a-specs-human",      href: "/specs#layer-human",      label: "Specs · human & governance",kind: "anchor" },
];

/* ── compose the registry ─────────────────────────────── */

export const MARKS: MarkedPlace[] = [
  ...PRIMARY_PAGES,
  ...LEARN_PAGES,
  ...SPECS_PAGES,
  ...IMPLEMENT_PAGES,
  ...ENTERPRISE_PAGES,
  ...RESEARCH_PAGES,
  ...CANON_PAGES,
  ...ADVOCACY_PAGES,
  ...VOYAGE_PAGES,
  ...INTERNAL_WRITING_PAGES,
  ...LANGUAGE_PAGES,
  ...ROOM_PAGES,
  ...AXIOM_PAGES,
  ...STAGE_MARKS,
  ...PAPER_MARKS,
  ...FALSIFIER_MARKS,
  ...ANCHOR_MARKS,
];

/** The aspirational ceiling. Grow ANCHOR_MARKS to approach this. */
export const TARGET_COUNT = 777;
